import axios from 'axios';

/** Si REACT_APP_BACKEND_URL está vacío (producción con proxy nginx), usar /api en el mismo host. */
export function getApiBase() {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (raw == null || String(raw).trim() === '') {
    return '/api';
  }
  return `${String(raw).replace(/\/$/, '')}/api`;
}

/** Para rutas de imágenes absolutas; vacío => rutas relativas (/api/product-images/...). */
export function getPublicApiOrigin() {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (raw == null || String(raw).trim() === '') {
    return '';
  }
  return String(raw).replace(/\/$/, '');
}

/** URL utilizable en <img src> (absolutas, /api/... o rutas relativas con proxy). */
export function resolveMediaUrl(url) {
  if (url == null || String(url).trim() === '') return '';
  const u = String(url).trim();
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const path = u.startsWith('/') ? u : `/${u}`;
  const origin = getPublicApiOrigin();
  return origin ? `${origin}${path}` : path;
}

const API = getApiBase();

/** Cliente HTTP compartido (cookies). Usado por Super SaaS y paneles de suscripción. */
export const apiClient = axios.create({
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const headers = config.headers || {};
  if (!headers.Authorization) {
    const token = localStorage.getItem('token') || localStorage.getItem('super_token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  config.headers = headers;
  return config;
});

/** Convierte `detail` de FastAPI (string, array Pydantic u objeto) a texto para toasts. */
export function formatApiDetail(detail, fallback = 'Error en la solicitud') {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && item.msg) {
          const loc = Array.isArray(item.loc) ? item.loc.filter((x) => x !== 'body').join('.') : '';
          return loc ? `${loc}: ${item.msg}` : String(item.msg);
        }
        return null;
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : fallback;
  }
  if (typeof detail === 'object' && detail.msg) return String(detail.msg);
  try {
    return JSON.stringify(detail);
  } catch {
    return fallback;
  }
}

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Products
export const getProducts = async (activeOnly = true) => {
  const response = await axios.get(`${API}/products?active_only=${activeOnly}`);
  return response.data;
};

export const getProduct = async (id) => {
  const response = await axios.get(`${API}/products/${id}`);
  return response.data;
};

export const createProduct = async (product) => {
  const response = await axios.post(`${API}/admin/products`, product, { headers: getHeaders() });
  return response.data;
};

export const updateProduct = async (id, product) => {
  const response = await axios.put(`${API}/admin/products/${id}`, product, { headers: getHeaders() });
  return response.data;
};

export const deleteProduct = async (id) => {
  const response = await axios.delete(`${API}/admin/products/${id}`, { headers: getHeaders() });
  return response.data;
};

export const uploadProductImage = async (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/admin/products/${id}/image`, formData, {
    headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Appointments
export const getAvailableSlots = async (date) => {
  const response = await axios.get(`${API}/available-slots?date=${date}`);
  return response.data;
};

export const createAppointment = async (appointment) => {
  const response = await axios.post(`${API}/appointments`, appointment);
  return response.data;
};

export const createAdminAppointment = async (appointment) => {
  const response = await axios.post(`${API}/admin/appointments`, appointment, { headers: getHeaders() });
  return response.data;
};

export const getAppointments = async (status = null) => {
  const url = status ? `${API}/appointments?status=${status}` : `${API}/appointments`;
  const response = await axios.get(url, { headers: getHeaders() });
  return response.data;
};

export const updateAppointment = async (id, data) => {
  const response = await axios.put(`${API}/admin/appointments/${id}`, data, { headers: getHeaders() });
  return response.data;
};

/** Eventos: `start_at` / `end_at` en RFC3339 UTC. Citas Mongo: `date`+`time` en CALENDAR_TIMEZONE (ver backend). */
export const getAdminCalendarEvents = async (startAt, endAt) => {
  const response = await axios.get(
    `${API}/admin/calendar-events?start_at=${encodeURIComponent(startAt)}&end_at=${encodeURIComponent(endAt)}`,
    { headers: getHeaders() }
  );
  return response.data;
};

// Reviews
export const getReviews = async (approvedOnly = true) => {
  const response = await axios.get(`${API}/reviews?approved_only=${approvedOnly}`);
  return response.data;
};

export const getAllReviews = async (status = null) => {
  const url = status ? `${API}/admin/reviews?status=${status}` : `${API}/admin/reviews`;
  const response = await axios.get(url, { headers: getHeaders() });
  return response.data;
};

export const createReview = async (review) => {
  const response = await axios.post(`${API}/reviews`, review);
  return response.data;
};

export const updateReview = async (id, data) => {
  const response = await axios.put(`${API}/admin/reviews/${id}`, data, { headers: getHeaders() });
  return response.data;
};

export const deleteReview = async (id) => {
  const response = await axios.delete(`${API}/admin/reviews/${id}`, { headers: getHeaders() });
  return response.data;
};

// Schedule
export const getSchedule = async () => {
  const response = await axios.get(`${API}/schedule`);
  return response.data;
};

export const updateSchedule = async (schedule) => {
  const response = await axios.put(`${API}/admin/schedule`, schedule, { headers: getHeaders() });
  return response.data;
};

export const getCalendarExceptions = async () => {
  const response = await axios.get(`${API}/calendar-exceptions`);
  return response.data;
};

export const createCalendarException = async (exception) => {
  const response = await axios.post(`${API}/admin/calendar-exceptions`, exception, { headers: getHeaders() });
  return response.data;
};

export const deleteCalendarException = async (id) => {
  const response = await axios.delete(`${API}/admin/calendar-exceptions/${id}`, { headers: getHeaders() });
  return response.data;
};

// Clinic Info
export const getClinicInfo = async () => {
  const response = await axios.get(`${API}/clinic-info`);
  return response.data;
};

export const updateClinicInfo = async (info) => {
  const response = await axios.put(`${API}/admin/clinic-info`, info, { headers: getHeaders() });
  return response.data;
};

/** Sube imagen JPG/PNG/GIF/WebP para sitio público. Respuesta { image_url }. Requiere super_admin. */
export const uploadClinicSiteImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/admin/clinic-site-image`, formData, {
    headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/** Solo super_admin */
export const getAdminUsers = async () => {
  const response = await axios.get(`${API}/admin/admin-users`, { headers: getHeaders() });
  return response.data;
};

export const createAdminUser = async (payload) => {
  const response = await axios.post(`${API}/admin/admin-users`, payload, { headers: getHeaders() });
  return response.data;
};

export const deleteAdminUser = async (userId) => {
  const response = await axios.delete(`${API}/admin/admin-users/${userId}`, { headers: getHeaders() });
  return response.data;
};

export const updateAdminUserPassword = async (userId, password) => {
  const response = await axios.patch(
    `${API}/admin/admin-users/${userId}/password`,
    { password },
    { headers: getHeaders() }
  );
  return response.data;
};

export const updateAdminUser = async (userId, payload) => {
  const response = await axios.patch(`${API}/admin/admin-users/${userId}`, payload, {
    headers: getHeaders(),
  });
  return response.data;
};

export const getAdminSettings = async () => {
  const response = await axios.get(`${API}/admin/settings`, { headers: getHeaders() });
  return response.data;
};

export const updateAdminSettings = async (settings) => {
  const response = await axios.put(`${API}/admin/settings`, settings, { headers: getHeaders() });
  return response.data;
};

/** Imagen opcional del menú/saludo del árbol WhatsApp. Respuesta { image_url }. Requiere admin. */
export const uploadWhatsappTreeGreetingImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/admin/whatsapp-tree-greeting-image`, formData, {
    headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/** Solo super_admin. Envía recordatorios matutinos del día (WhatsApp). */
export const sendDailyRemindersNow = async ({ force = false } = {}) => {
  const response = await axios.post(
    `${API}/admin/reminders/daily-send`,
    { force: Boolean(force) },
    { headers: getHeaders() }
  );
  return response.data;
};

/** Frase exacta que debe enviarse en confirm_phrase (coincide con el backend). */
export const SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE = 'BORRAR TODAS LAS CITAS';

export const SUPER_RESET_ORDERS_CONFIRM_PHRASE = 'BORRAR TODOS LOS PEDIDOS';

/** Solo super_admin. Borra todos los pedidos y reinicia el contador (irreversible). */
export const superAdminResetAllOrders = async (confirmPhrase) => {
  const response = await axios.post(
    `${API}/admin/super/reset-orders`,
    { confirm_phrase: String(confirmPhrase || '').trim() },
    { headers: getHeaders() }
  );
  return response.data;
};

/** Solo super_admin. Borra todas las citas en MongoDB (irreversible). */
export const superAdminResetAllAppointments = async (confirmPhrase) => {
  const response = await axios.post(
    `${API}/admin/super/reset-appointments`,
    { confirm_phrase: String(confirmPhrase || '').trim() },
    { headers: getHeaders() }
  );
  return response.data;
};

// Stats
export const getAdminStats = async () => {
  const response = await axios.get(`${API}/admin/stats`, { headers: getHeaders() });
  return response.data;
};

/** Estado del puente Baileys (vinculado, QR data URL, etc.). Requiere admin. */
export const getWhatsAppLinkStatus = async () => {
  const response = await axios.get(`${API}/admin/whatsapp-link`, { headers: getHeaders() });
  return response.data;
};

/** Reinicia sesión del puente (nuevo QR). Requiere admin. */
export const resetWhatsAppBridgeSession = async () => {
  const response = await axios.post(`${API}/admin/whatsapp-link/reset`, {}, { headers: getHeaders() });
  return response.data;
};

/** Reinicia estado del bot en Redis (menú, carrito, repetir pedido). No desvincula WhatsApp. */
export const clearWhatsAppBotState = async () => {
  const response = await axios.post(`${API}/admin/whatsapp/clear-bot-state`, {}, { headers: getHeaders() });
  return response.data;
};

/** Envío manual a un número (vía puente Baileys). Requiere admin y sesión WhatsApp vinculada. */
export const sendWhatsAppAdmin = async (phone, message) => {
  const response = await axios.post(
    `${API}/admin/whatsapp-send`,
    { phone, message },
    { headers: getHeaders() }
  );
  return response.data;
};

/** Estado de envío WhatsApp por requestId: queued/sent/delivered/read/failed. */
export const getWhatsAppSendStatus = async (requestId) => {
  const response = await axios.get(`${API}/admin/whatsapp-send/${encodeURIComponent(requestId)}`, {
    headers: getHeaders()
  });
  return response.data;
};

// --- Pizas: menú y pedidos ---
export const getPublicMenu = async () => {
  const response = await axios.get(`${API}/menu`);
  return response.data;
};

export const getStoreStatus = async () => {
  const response = await axios.get(`${API}/public/store-status`);
  return response.data;
};

export const getAdminOrderPaymentLink = async (orderId) => {
  const response = await axios.post(
    `${API}/admin/orders/${orderId}/payment-link`,
    {},
    { headers: getHeaders() }
  );
  return response.data;
};

export const getEtaPreview = async () => {
  const response = await axios.get(`${API}/orders/eta-preview`);
  return response.data;
};

export const createOrder = async (payload) => {
  const response = await axios.post(`${API}/orders`, payload);
  return response.data;
};

export const createMercadoPagoCheckout = async (payload) => {
  const response = await axios.post(`${API}/orders/mercadopago-checkout`, payload);
  return response.data;
};

export const getCheckoutStatus = async (checkoutId) => {
  const response = await axios.get(`${API}/checkouts/${checkoutId}`);
  return response.data;
};

export const getOrder = async (orderId) => {
  const response = await axios.get(`${API}/orders/${orderId}`);
  return response.data;
};

export const submitPaymentProof = async (
  orderId,
  { payment_reference = '', file = null, transfer_proof_consent = false, privacy_notice_version = '' } = {},
) => {
  const ref = String(payment_reference || '').trim();
  if (file) {
    const form = new FormData();
    if (ref) form.append('payment_reference', ref);
    form.append('transfer_proof_consent', transfer_proof_consent ? 'true' : 'false');
    if (privacy_notice_version) form.append('privacy_notice_version', privacy_notice_version);
    form.append('file', file);
    const response = await axios.post(`${API}/orders/${orderId}/payment-proof`, form);
    return response.data;
  }
  const response = await axios.post(`${API}/orders/${orderId}/payment-proof`, {
    payment_reference: ref,
    transfer_proof_consent: Boolean(transfer_proof_consent),
    privacy_notice_version: privacy_notice_version || '',
  });
  return response.data;
};

export const getTransferProofs = async (date = '', pending = true) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (pending) params.set('pending', '1');
  const q = params.toString() ? `?${params.toString()}` : '';
  const response = await axios.get(`${API}/admin/transfer-proofs${q}`, { headers: getHeaders() });
  return response.data;
};

export const confirmTransferProof = async (proofId) => {
  const response = await axios.post(
    `${API}/admin/transfer-proofs/${encodeURIComponent(proofId)}/confirm`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const linkTransferProofToOrder = async (proofId, orderId) => {
  const response = await axios.post(
    `${API}/admin/transfer-proofs/${encodeURIComponent(proofId)}/link-order`,
    { order_id: orderId },
    { headers: getHeaders() },
  );
  return response.data;
};

export const rejectTransferProof = async (proofId) => {
  const response = await axios.post(
    `${API}/admin/transfer-proofs/${encodeURIComponent(proofId)}/reject`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const fetchTransferProofImageUrl = async (proofId) => {
  const response = await axios.get(
    `${API}/admin/transfer-proofs/${encodeURIComponent(proofId)}/image`,
    { headers: getHeaders(), responseType: 'blob' },
  );
  return URL.createObjectURL(response.data);
};

export const getOrderTransferProof = async (orderId) => {
  const response = await axios.get(
    `${API}/admin/orders/${encodeURIComponent(orderId)}/transfer-proof`,
    { headers: getHeaders() },
  );
  return response.data;
};

export const getOrderReceipt = async (orderId) => {
  const response = await axios.get(
    `${API}/admin/orders/${encodeURIComponent(orderId)}/receipt`,
    { headers: getHeaders() },
  );
  return response.data;
};

export const uploadReceiptTicketImage = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await axios.post(`${API}/admin/pizas/receipt-ticket-image`, form, {
    headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const getOrderPaymentUrl = async (orderId) => {
  const response = await axios.get(`${API}/orders/${orderId}/payment-url`);
  return response.data;
};

export const cancelOrder = async (orderId) => {
  const response = await axios.post(`${API}/orders/${orderId}/cancel`);
  return response.data;
};

export const cancelCheckout = async (checkoutId) => {
  const response = await axios.post(`${API}/checkouts/${checkoutId}/cancel`);
  return response.data;
};

export const createAdminCounterOrder = async (payload) => {
  const response = await axios.post(`${API}/admin/orders/counter`, payload, {
    headers: getHeaders(),
  });
  return response.data;
};

export const resendTransferWhatsapp = async (orderId) => {
  const response = await axios.post(
    `${API}/admin/orders/${orderId}/resend-transfer-whatsapp`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const getAdminOrders = async (status = '', date = '') => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (date) params.set('date', date);
  const q = params.toString() ? `?${params.toString()}` : '';
  const response = await axios.get(`${API}/admin/orders${q}`, { headers: getHeaders() });
  return response.data;
};

export const updateAdminOrder = async (id, data) => {
  const response = await axios.put(`${API}/admin/orders/${id}`, data, { headers: getHeaders() });
  return response.data;
};

export const getKitchenOrders = async () => {
  const response = await axios.get(`${API}/admin/kitchen/orders`, { headers: getHeaders() });
  const data = response.data;
  if (Array.isArray(data)) {
    const orders = data;
    const queue_pizza_count = orders.reduce((sum, o) => {
      const lines = o.kitchen_lines || o.lines || [];
      return (
        sum
        + lines.reduce((s, ln) => s + Math.max(1, Number(ln.qty) || 1), 0)
      );
    }, 0);
    return { orders, queue_pizza_count, queue_order_count: orders.length };
  }
  return {
    orders: Array.isArray(data?.orders) ? data.orders : [],
    queue_pizza_count: Number(data?.queue_pizza_count) || 0,
    queue_order_count: Number(data?.queue_order_count) || 0,
  };
};

export const markKitchenOrderAttended = async (orderId) => {
  const response = await axios.post(
    `${API}/admin/kitchen/orders/${orderId}/mark-attended`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const getComplementMenu = async () => {
  const response = await axios.get(`${API}/admin/complement-menu`, { headers: getHeaders() });
  return response.data;
};

export const addOrderComplements = async (orderId, lines) => {
  const response = await axios.post(
    `${API}/admin/orders/${orderId}/add-complements`,
    { lines },
    { headers: getHeaders() },
  );
  return response.data;
};

export const fulfillPendingOrderExtras = async (orderId) => {
  const response = await axios.post(
    `${API}/admin/orders/${orderId}/fulfill-pending-extras`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const removeOrderLines = async (orderId, lineIndices) => {
  const response = await axios.post(
    `${API}/admin/orders/${orderId}/remove-lines`,
    { line_indices: lineIndices },
    { headers: getHeaders() },
  );
  return response.data;
};

export const getMpPayments = async (date = '', unmatched = true) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (unmatched) params.set('unmatched', '1');
  const q = params.toString() ? `?${params.toString()}` : '';
  const response = await axios.get(`${API}/admin/mp-payments${q}`, { headers: getHeaders() });
  return response.data;
};

export const linkMpPaymentToOrder = async (paymentId, orderId) => {
  const response = await axios.post(
    `${API}/admin/mp-payments/${encodeURIComponent(paymentId)}/link-order`,
    { order_id: orderId },
    { headers: getHeaders() },
  );
  return response.data;
};

export const syncAdminOrderMercadoPago = async (orderId) => {
  const response = await axios.post(
    `${API}/admin/orders/${orderId}/sync-mercadopago`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const deleteAdminOrder = async (orderId, motivo = '') => {
  // Borrado lógico (solo administradores) [eliminar-pedidos-20261020]
  const response = await axios.delete(`${API}/admin/orders/${orderId}`, {
    headers: getHeaders(),
    data: motivo ? { motivo } : undefined,
  });
  return response.data;
};

export const syncMercadoPagoByOrderNumber = async (orderNumber) => {
  const response = await axios.post(
    `${API}/admin/mercadopago/sync-by-number`,
    { order_number: orderNumber },
    { headers: getHeaders() },
  );
  return response.data;
};

// voz-0052-20261020: cola de impresión automática de pedidos en línea (web/WhatsApp/teléfono)
export const staffOnlinePendingPrint = async () => {
  const response = await axios.get(`${API}/staff/online/pending-print`, { headers: getHeaders() });
  return response.data;
};

export const getAdminOrderStats = async (date = '') => {
  // date: 'YYYY-MM-DD' (un día) o { from, to } (rango inclusivo, días de México) [admin-dashboard-20261020]
  let q = '';
  if (date && typeof date === 'object') {
    const p = new URLSearchParams();
    if (date.from) p.set('date_from', date.from);
    if (date.to) p.set('date_to', date.to);
    q = `?${p.toString()}`;
  } else if (date) {
    q = `?date=${encodeURIComponent(date)}`;
  }
  const response = await axios.get(`${API}/admin/orders/stats${q}`, { headers: getHeaders() });
  return response.data;
};

export const getDeliveryDrivers = async (activeOnly = false) => {
  const q = activeOnly ? '?active_only=true' : '';
  const response = await axios.get(`${API}/admin/delivery-drivers${q}`, { headers: getHeaders() });
  return response.data;
};

export const createDeliveryDriver = async (data) => {
  const response = await axios.post(`${API}/admin/delivery-drivers`, data, { headers: getHeaders() });
  return response.data;
};

export const updateDeliveryDriver = async (id, data) => {
  const response = await axios.put(`${API}/admin/delivery-drivers/${id}`, data, {
    headers: getHeaders(),
  });
  return response.data;
};

export const patchDeliveryDriverActive = async (id, active) => {
  const response = await axios.patch(
    `${API}/admin/delivery-drivers/${id}/active`,
    { active },
    { headers: getHeaders() },
  );
  return response.data;
};

export const deleteDeliveryDriver = async (id) => {
  const response = await axios.delete(`${API}/admin/delivery-drivers/${id}`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const assignOrderDriver = async (orderId, driverId) => {
  const response = await axios.post(
    `${API}/admin/orders/${orderId}/assign-driver`,
    { driver_id: driverId },
    { headers: getHeaders() },
  );
  return response.data;
};

export const notifyDriverWhatsapp = async (orderId) => {
  const response = await axios.post(
    `${API}/admin/orders/${orderId}/notify-driver`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const getPizasSettings = async () => {
  const response = await axios.get(`${API}/admin/pizas/settings`, { headers: getHeaders() });
  return response.data;
};

export const getPizasUiConfig = async () => {
  const response = await axios.get(`${API}/admin/pizas/ui-config`, { headers: getHeaders() });
  return response.data;
};

export const updatePizasSettings = async (data) => {
  const response = await axios.put(`${API}/admin/pizas/settings`, data, { headers: getHeaders() });
  return response.data;
};

export const getMpDiagnostics = async () => {
  const response = await axios.get(`${API}/admin/pizas/mp-diagnostics`, { headers: getHeaders() });
  return response.data;
};

export const getPromoCodes = async () => {
  const response = await axios.get(`${API}/admin/pizas/promo-codes`, { headers: getHeaders() });
  return response.data;
};

export const createPromoCode = async (data) => {
  const response = await axios.post(`${API}/admin/pizas/promo-codes`, data, { headers: getHeaders() });
  return response.data;
};

export const updatePromoCode = async (id, data) => {
  const response = await axios.patch(`${API}/admin/pizas/promo-codes/${id}`, data, {
    headers: getHeaders(),
  });
  return response.data;
};

export const deletePromoCode = async (id) => {
  const response = await axios.delete(`${API}/admin/pizas/promo-codes/${id}`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const validatePromoCode = async (payload) => {
  const response = await axios.post(`${API}/promo/validate`, payload);
  return response.data;
};

export const getPizasWaBlacklist = async () => {
  const response = await axios.get(`${API}/admin/pizas/wa-blacklist`, { headers: getHeaders() });
  return response.data;
};

export const updatePizasWaBlacklist = async (entries) => {
  const response = await axios.put(
    `${API}/admin/pizas/wa-blacklist`,
    { entries },
    { headers: getHeaders() }
  );
  return response.data;
};

// listas-20261022: lista blanca de WhatsApp y filtro de llamadas
export const getPizasWaWhitelist = async () => {
  const response = await axios.get(`${API}/admin/pizas/wa-whitelist`, { headers: getHeaders() });
  return response.data;
};

export const updatePizasWaWhitelist = async (patch) => {
  const response = await axios.put(`${API}/admin/pizas/wa-whitelist`, patch, { headers: getHeaders() });
  return response.data;
};

export const getPizasCallScreening = async () => {
  const response = await axios.get(`${API}/admin/pizas/call-screening`, { headers: getHeaders() });
  return response.data;
};

export const updatePizasCallScreening = async (patch) => {
  const response = await axios.put(`${API}/admin/pizas/call-screening`, patch, { headers: getHeaders() });
  return response.data;
};

export const getAdminMenuItems = async () => {
  const response = await axios.get(`${API}/admin/menu-items`, { headers: getHeaders() });
  return response.data;
};

export const getAdminMenuCategories = async () => {
  const response = await axios.get(`${API}/admin/menu-categories`, { headers: getHeaders() });
  return response.data;
};

export const getAdminIngredients = async () => {
  const response = await axios.get(`${API}/admin/ingredients`, { headers: getHeaders() });
  return response.data;
};

export const createMenuCategory = async (data) => {
  const response = await axios.post(`${API}/admin/menu-categories`, data, { headers: getHeaders() });
  return response.data;
};

export const updateMenuCategory = async (id, data) => {
  const response = await axios.put(`${API}/admin/menu-categories/${id}`, data, { headers: getHeaders() });
  return response.data;
};

export const deleteMenuCategory = async (id) => {
  const response = await axios.delete(`${API}/admin/menu-categories/${id}`, { headers: getHeaders() });
  return response.data;
};

export const createMenuItem = async (data) => {
  const response = await axios.post(`${API}/admin/menu-items`, data, { headers: getHeaders() });
  return response.data;
};

export const updateMenuItem = async (id, data) => {
  const response = await axios.put(`${API}/admin/menu-items/${id}`, data, { headers: getHeaders() });
  return response.data;
};

export const deleteMenuItem = async (id) => {
  const response = await axios.delete(`${API}/admin/menu-items/${id}`, { headers: getHeaders() });
  return response.data;
};

export const uploadMenuItemImage = async (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/admin/menu-items/${id}/image`, formData, {
    headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const createIngredient = async (data) => {
  const response = await axios.post(`${API}/admin/ingredients`, data, { headers: getHeaders() });
  return response.data;
};

export const updateIngredient = async (id, data) => {
  const response = await axios.put(`${API}/admin/ingredients/${id}`, data, { headers: getHeaders() });
  return response.data;
};

export const deleteIngredient = async (id) => {
  const response = await axios.delete(`${API}/admin/ingredients/${id}`, { headers: getHeaders() });
  return response.data;
};

export const getAdminInventory = async (lowStockOnly = false) => {
  const response = await axios.get(`${API}/admin/inventory`, {
    headers: getHeaders(),
    params: { low_stock_only: lowStockOnly },
  });
  return response.data;
};

export const adjustInventoryStock = async (
  ingredientId,
  { deltaKg, lowStockKg, deltaUnits, lowStockUnits, note = '' } = {},
) => {
  const body = { note };
  if (deltaUnits != null && deltaUnits !== '' && Number(deltaUnits) !== 0) {
    body.delta_units = Number(deltaUnits);
  } else if (deltaKg != null && deltaKg !== '' && Number(deltaKg) !== 0) {
    body.delta_kg = Number(deltaKg);
  }
  if (lowStockUnits != null && lowStockUnits !== '') {
    body.low_stock_units = Number(lowStockUnits);
  } else if (lowStockKg != null && lowStockKg !== '') {
    body.low_stock_kg = Number(lowStockKg);
  }
  const response = await axios.patch(`${API}/admin/inventory/${ingredientId}/stock`, body, {
    headers: getHeaders(),
  });
  return response.data;
};

export const seedBeverageInventory = async () => {
  const response = await axios.post(
    `${API}/admin/inventory/seed-beverages`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const getInventoryMovements = async (ingredientId = null, options = {}) => {
  const opts = typeof options === 'number' ? { limit: options } : options || {};
  const response = await axios.get(`${API}/admin/inventory/movements`, {
    headers: getHeaders(),
    params: {
      ingredient_id: ingredientId || undefined,
      limit: opts.limit ?? 100,
      date: opts.date || undefined,
      week: opts.week || undefined,
    },
  });
  return response.data;
};

export const getInventoryConsumption = async (date = null) => {
  const response = await axios.get(`${API}/admin/inventory/consumption`, {
    headers: getHeaders(),
    params: { date: date || undefined },
  });
  return response.data;
};

export const migrateMenuRecipes = async () => {
  const response = await axios.post(`${API}/admin/inventory/migrate-recipes`, {}, { headers: getHeaders() });
  return response.data;
};

// ---------- Staff comanda / caja / print ----------

export const staffGetConfig = async () => {
  const response = await axios.get(`${API}/staff/config`, { headers: getHeaders() });
  return response.data;
};

export const staffSetOrdersAccepting = async (accepting, message) => {
  const response = await axios.put(
    `${API}/staff/orders-accepting`,
    { accepting, message },
    { headers: getHeaders() }
  );
  return response.data;
};

export const staffGetTables = async () => {
  const response = await axios.get(`${API}/staff/tables`, { headers: getHeaders() });
  return response.data;
};

export const staffGetMenu = async () => {
  const response = await axios.get(`${API}/staff/menu/categories`, { headers: getHeaders() });
  return response.data;
};

export const staffCreateTakeaway = async (customerName) => {
  const response = await axios.post(
    `${API}/staff/orders`,
    { customer_name: customerName },
    { headers: getHeaders() }
  );
  return response.data;
};

export const staffAddTableItem = async (tableId, body) => {
  const response = await axios.post(`${API}/staff/tables/${tableId}/items`, body, {
    headers: getHeaders(),
  });
  return response.data;
};

export const staffAddOrderItem = async (orderId, body) => {
  const response = await axios.post(`${API}/staff/orders/${orderId}/items`, body, {
    headers: getHeaders(),
  });
  return response.data;
};

export const staffSubmitOrder = async (orderId) => {
  const response = await axios.post(`${API}/staff/orders/${orderId}/submit`, {}, { headers: getHeaders() });
  return response.data;
};

export const staffIncLine = async (orderId, lineId) => {
  const response = await axios.post(
    `${API}/staff/orders/${orderId}/items/${lineId}/increment`,
    {},
    { headers: getHeaders() }
  );
  return response.data;
};

export const staffDecLine = async (orderId, lineId) => {
  const response = await axios.post(
    `${API}/staff/orders/${orderId}/items/${lineId}/decrement`,
    {},
    { headers: getHeaders() }
  );
  return response.data;
};

export const staffGetOpenOrders = async () => {
  const response = await axios.get(`${API}/staff/orders/open`, { headers: getHeaders() });
  return response.data;
};

export const staffCloseOrder = async (orderId, body = {}) => {
  const response = await axios.post(`${API}/staff/orders/${orderId}/close`, body, {
    headers: getHeaders(),
  });
  return response.data;
};

export const staffCancelOrder = async (orderId) => {
  const response = await axios.post(
    `${API}/staff/orders/${orderId}/cancel`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const staffCancelLine = async (orderId, lineId) => {
  const response = await axios.post(
    `${API}/staff/orders/${orderId}/items/${lineId}/cancel`,
    {},
    { headers: getHeaders() },
  );
  return response.data;
};

export const staffCashSession = async () => {
  const response = await axios.get(`${API}/staff/cash/session`, { headers: getHeaders() });
  return response.data;
};

export const staffCashOpen = async () => {
  const response = await axios.post(`${API}/staff/cash/session/open`, {}, { headers: getHeaders() });
  return response.data;
};

export const staffCashClose = async () => {
  const response = await axios.post(`${API}/staff/cash/session/close`, {}, { headers: getHeaders() });
  return response.data;
};

export const staffKitchenPendingPrint = async () => {
  const response = await axios.get(`${API}/staff/kitchen/pending-print?station=kitchen`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const staffCashierPendingPrint = async () => {
  const response = await axios.get(`${API}/staff/cashier/pending-print`, { headers: getHeaders() });
  return response.data;
};

export const staffPrintAck = async (orderId, station = 'kitchen') => {
  const response = await axios.post(
    `${API}/staff/orders/${orderId}/print-ack`,
    { station },
    { headers: getHeaders() }
  );
  return response.data;
};

export const adminListDiningTables = async () => {
  const response = await axios.get(`${API}/admin/dining-tables`, { headers: getHeaders() });
  return response.data;
};

export const adminCreateDiningTable = async (body) => {
  const response = await axios.post(`${API}/admin/dining-tables`, body, { headers: getHeaders() });
  return response.data;
};

export const adminUpdateDiningTable = async (id, body) => {
  const response = await axios.put(`${API}/admin/dining-tables/${id}`, body, { headers: getHeaders() });
  return response.data;
};

export const adminDeleteDiningTable = async (id) => {
  const response = await axios.delete(`${API}/admin/dining-tables/${id}`, { headers: getHeaders() });
  return response.data;
};

// --- Paquete legal v2026-10-09: identidad, consentimientos y baja de promociones ---
export const getPublicLegalIdentity = async () => {
  const response = await axios.get(`${API}/public/legal-identity`);
  return response.data;
};

export const getAdminLegalIdentity = async () => {
  const response = await axios.get(`${API}/admin/legal-identity`, { headers: getHeaders() });
  return response.data;
};

export const updateAdminLegalIdentity = async (data) => {
  const response = await axios.put(`${API}/admin/legal-identity`, data, { headers: getHeaders() });
  return response.data;
};

export const getAdminLegalCommercial = async () => {
  const response = await axios.get(`${API}/admin/pizas/legal-commercial`, { headers: getHeaders() });
  return response.data;
};

export const updateAdminLegalCommercial = async (data) => {
  const response = await axios.put(`${API}/admin/pizas/legal-commercial`, data, { headers: getHeaders() });
  return response.data;
};

export const getAdminConsents = async (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && String(v) !== '') q.set(k, String(v));
  });
  const qs = q.toString() ? `?${q.toString()}` : '';
  const response = await axios.get(`${API}/admin/consents${qs}`, { headers: getHeaders() });
  return response.data;
};

export const postMarketingBaja = async ({ phone = '', token = '' } = {}) => {
  const body = token ? { token } : { phone };
  const response = await axios.post(`${API}/public/marketing/baja`, body);
  return response.data;
};

export const getMarketingBajaTokenStatus = async (token) => {
  const response = await axios.get(`${API}/public/marketing/baja-token-status`, { params: { t: token } });
  return response.data;
};
